import axios from 'axios';
import { Capacitor, registerPlugin } from '@capacitor/core';
import { computeWake } from './computeWake';

// The native background-upload pipeline (CmUploadWorker, via WorkManager) makes
// its own authenticated API calls independent of the WebView/JS layer, so it
// needs its own copy of whichever token is currently active - pushed here,
// the one place both `api` and `orgContext` change the token, rather than
// scattered across every login/logout call site. No-ops on web/PWA.
const NativeFolderSync = registerPlugin('NativeFolderSync');
function syncNativeAuthToken() {
  if (!Capacitor.isNativePlatform()) return;
  const token = _getActiveToken();
  NativeFolderSync.syncAuthToken({ token: token || '' }).catch(() => {});
}

// ── Backend URLs ──────────────────────────────────────────────────────────────
//
// Two backends handle different route groups:
//
//   Lambda  (/api/*)        - auth, profile, network, notifications, media,
//                             billing, grouping lifecycle, /api/external/* identity
//                             routes.  Served via CloudFront → API Gateway → Lambda.
//
//   EC2     (/api/core/*)   - compute routes: event management, face grouping,
//                             CLIP/FAISS moments search.
//                             Served via CloudFront → EC2 Flask on :8000.
//
// Toggle with REACT_APP_BACKEND_MODE:
//   local  -> Lambda on localhost:8010, EC2 on localhost:8000
//   server -> CloudFront/custom domain at https://www.circlemoments.com
//
// package_frontend.cmd sets REACT_APP_BACKEND_MODE=server before building.

const BACKEND_MODE = (process.env.REACT_APP_BACKEND_MODE || 'local').toLowerCase();
const SERVER_ORIGIN = (process.env.REACT_APP_SERVER_BACKEND_ORIGIN || 'https://www.circlemoments.com').replace(/\/+$/, '');
const LOCAL_LAMBDA_ORIGIN = (process.env.REACT_APP_LOCAL_LAMBDA_ORIGIN || 'http://localhost:8010').replace(/\/+$/, '');
const LOCAL_CORE_ORIGIN = (process.env.REACT_APP_LOCAL_CORE_ORIGIN || 'http://localhost:8000').replace(/\/+$/, '');
const isServerMode = BACKEND_MODE === 'server';

// Raw origin URLs used for <img src="...?token="> direct-fetch patterns.
export const BACKEND_URL = isServerMode ? SERVER_ORIGIN : LOCAL_LAMBDA_ORIGIN;
export const CORE_BACKEND_URL = isServerMode ? SERVER_ORIGIN : LOCAL_CORE_ORIGIN;

const LAMBDA_BASE_URL = BACKEND_URL + '/api';
const CORE_BASE_URL = CORE_BACKEND_URL + '/api/core';
const EXTERNAL_BASE_URL = BACKEND_URL + '/api/external'; // migrated to Lambda (Group A)


// ── Axios instances ───────────────────────────────────────────────────────────

// ── Org context helpers ───────────────────────────────────────────────────────
// When the user switches to an org, an org-context JWT is stored separately.
// The active context is tracked so the interceptor can pick the right token.

export const orgContext = {
  getToken:      () => localStorage.getItem('org_auth_token'),
  setToken:      (t) => { localStorage.setItem('org_auth_token', t); syncNativeAuthToken(); },
  clearToken:    () => { localStorage.removeItem('org_auth_token'); syncNativeAuthToken(); },
  getActiveOrg:  () => { try { return JSON.parse(localStorage.getItem('active_org') || 'null'); } catch { return null; } },
  setActiveOrg:  (o) => localStorage.setItem('active_org', JSON.stringify(o)),
  clearActiveOrg:() => localStorage.removeItem('active_org'),
  isActive:      () => !!localStorage.getItem('org_auth_token'),
};

function _getActiveToken() {
  if (orgContext.isActive()) return orgContext.getToken();
  return localStorage.getItem('auth_token');
}

// Backend EC2 stopped (idle auto-stop) - CloudFront returns 502/503/504 when
// there's nothing listening at the origin, or the request errors out with no
// response at all. Waits for EC2WakeOverlay to confirm the instance is healthy
// again, then replays the exact request that failed - the caller's original
// await/.then sees a (slow) successful response, as if it had been up the
// whole time. If the user closes the popup first, falls back to the original
// error instead.
async function _replayAfterWake(client, error) {
  try {
    await computeWake.waitForWake();
  } catch {
    return _rejectWithShape(error);
  }
  return client.request(error.config);
}

function _rejectWithShape(error) {
  if (error.response) {
    const { status, data } = error.response;
    return Promise.reject({
      status,
      message: data.message || data.error || 'An error occurred',
      data,
    });
  }
  return Promise.reject(error);
}

function _makeClient(baseURL, { handleUnreachable } = {}) {
  const client = axios.create({
    baseURL,
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
  });

  client.interceptors.request.use((config) => {
    const token = _getActiveToken();
    if (token) config.headers.Authorization = `Bearer ${token}`;
    return config;
  });

  client.interceptors.response.use(
    (response) => response,
    (error) => {
      if (error.response) {
        const { status } = error.response;
        if (status === 401) {
          const isAuthRoute = window.location.pathname.startsWith('/auth');
          if (!isAuthRoute) {
            // NOT a window.location.href hard redirect: that destroys the
            // current document, which blows away the pushState-based history
            // the native app's hardware back button relies on (see App.js's
            // BackExitGuard) - the WebView then can't soft-navigate back to
            // wherever the user came from and the back button gets stuck.
            // AuthContext (mounted for the whole app) and a Router-scoped
            // bridge (inside <Router>, see App.js's SessionExpiredRedirect)
            // both listen for this to do the actual state-clear + SPA
            // navigate.
            window.dispatchEvent(new Event('cm:session-expired'));
          }
        }
        if (handleUnreachable && [502, 503, 504].includes(status)) {
          return _replayAfterWake(client, error);
        }
        return _rejectWithShape(error);
      }
      // No response at all (network error / connection refused) - same cause.
      if (handleUnreachable) {
        return _replayAfterWake(client, error);
      }
      return Promise.reject(error);
    }
  );

  return client;
}

// Lambda client - used for lightweight /api/* routes.
export const apiClient = _makeClient(LAMBDA_BASE_URL);

// EC2 client - used for heavy /api/core/* routes (events, moments).
// handleUnreachable wires up the "scaling from zero" wake overlay + request
// replay - only relevant in server mode, where the EC2 backend can be auto-
// stopped when idle. In local mode a down :8000 just means the dev hasn't
// started it.
export const coreApiClient = _makeClient(CORE_BASE_URL, {
  handleUnreachable: isServerMode,
});

// EC2 client for third-party calls that need outbound internet.
export const externalApiClient = _makeClient(EXTERNAL_BASE_URL);

// ── Auth helpers (Lambda) ─────────────────────────────────────────────────────

const api = {
  checkCustomer: (phoneNumber, countryCode = '+44') =>
    apiClient.post('/check_customer', { phone_number: phoneNumber, country_code: countryCode }).then((r) => r.data),

  sendOTP: (phoneNumber, countryCode = '+44') =>
    externalApiClient.post('/send_otp', { phone_number: phoneNumber, country_code: countryCode }).then((r) => r.data),

  googleAuth: async (token) => {
    const response = await externalApiClient.post('/googleauth', { token });
    if (response.data.token) api.setToken(response.data.token);
    return response.data;
  },

  googleAuthSignup: (payload) =>
    externalApiClient.post('/googleauthsignup', payload).then((r) => r.data),

  loginOTP: async (phoneNumber, otp, countryCode = '+44') => {
    const response = await apiClient.post('/login_otp', { phone_number: phoneNumber, country_code: countryCode, otp });
    if (response.data.token) api.setToken(response.data.token);
    return response.data;
  },

  loginPassword: async (phoneNumber, password, countryCode = '+44') => {
    const response = await apiClient.post('/login_password', { phone_number: phoneNumber, country_code: countryCode, password });
    if (response.data.token) api.setToken(response.data.token);
    return response.data;
  },

  createPassword: async (phoneNumber, password, otp, countryCode = '+44') => {
    const response = await apiClient.post('/create_password', { phone_number: phoneNumber, country_code: countryCode, password, otp });
    if (response.data.token) api.setToken(response.data.token);
    return response.data;
  },

  passkeyLoginStart: (phoneNumber) =>
    apiClient.post('/passkey/login/start', { phone_number: phoneNumber, client_origin: window.location.origin }).then(r => r.data),

  passkeyLoginComplete: (phoneNumber, assertion) =>
    apiClient.post('/passkey/login/complete', { phone_number: phoneNumber, ...assertion, client_origin: window.location.origin }).then(r => r.data),

  // ── Email auth ───────────────────────────────────────────────────────────────
  sendEmailOtp: (email) =>
    externalApiClient.post('/email_otp/send', { email }).then(r => r.data),

  sendEmailForgotPassword: (email) =>
    externalApiClient.post('/email_forgot_password', { email }).then(r => r.data),

  emailResetPassword: async (email, otp, password) => {
    const response = await apiClient.post('/email_reset_password', { email, otp, password });
    if (response.data.token) api.setToken(response.data.token);
    return response.data;
  },

  emailSignup: async (email, password, name, otp) => {
    const response = await apiClient.post('/email_signup', { email, password, name, otp });
    if (response.data.token) api.setToken(response.data.token);
    return response.data;
  },

  emailLogin: async (email, password) => {
    const response = await apiClient.post('/email_login', { email, password });
    if (response.data.token) api.setToken(response.data.token);
    return response.data;
  },

  checkEmail: (email) =>
    apiClient.post('/check_customer', { email }).then(r => r.data),

  // ── Firebase Phone Auth ───────────────────────────────────────────────────
  // Called after Firebase OTP is confirmed client-side.  The backend verifies
  // the Firebase ID token and issues a CircleMoments JWT.
  loginFirebasePhone: async (phoneNumber, countryCode = '+44', firebaseIdToken) => {
    const response = await externalApiClient.post('/login_firebase_phone', {
      phone_number:      phoneNumber,
      country_code:      countryCode,
      firebase_id_token: firebaseIdToken,
    });
    if (response.data.token) api.setToken(response.data.token);
    return response.data;
  },

  // JWT-authenticated path for setting a password after Firebase phone login
  // (no OTP in localStorage needed — the JWT from loginFirebasePhone is used).
  createPasswordJwt: async (phoneNumber, password, countryCode = '+44') => {
    const response = await apiClient.post('/create_password', {
      phone_number: phoneNumber,
      country_code: countryCode,
      password,
    });
    return response.data;
  },

  getToken:   () => localStorage.getItem('auth_token'),
  setToken:   (token) => { localStorage.setItem('auth_token', token); syncNativeAuthToken(); },
  clearToken: () => { localStorage.removeItem('auth_token'); syncNativeAuthToken(); },
  logout:     () => api.clearToken(),

  // ── Organisation routes ────────────────────────────────────────────────────

  // Always uses the personal token regardless of active org context — needed by
  // UserAccountMenu to list orgs even when the user is already in an org context.
  listMyOrgsPersonal: () => {
    const personalToken = localStorage.getItem('auth_token');
    return apiClient.get('/organisations', {
      headers: { Authorization: `Bearer ${personalToken}` },
    }).then(r => r.data);
  },

  submitOrgCreation: (payload) =>
    apiClient.post('/org/requests/create', payload).then(r => r.data),

  submitOrgJoin: (payload) =>
    apiClient.post('/org/requests/join', payload).then(r => r.data),

  getOrgRequestStatus: () =>
    apiClient.get('/org/requests/status').then(r => r.data),

  switchOrgContext: (orgId) => {
    // Always use the personal token — never the active org token — so the
    // switch endpoint receives the personal customer_id for the membership lookup.
    const personalToken = localStorage.getItem('auth_token');
    return apiClient.post(`/org/auth/switch/${orgId}`, {}, {
      headers: { Authorization: `Bearer ${personalToken}` },
    }).then(r => r.data);
  },

  listMyOrgs: () =>
    apiClient.get('/organisations').then(r => r.data),

  getOrg: (orgId) =>
    apiClient.get(`/organisations/${orgId}`).then(r => r.data),

  updateOrg: (orgId, payload) =>
    apiClient.put(`/organisations/${orgId}`, payload).then(r => r.data),

  getOrgMembers: (orgId) =>
    apiClient.get(`/organisations/${orgId}/members`).then(r => r.data),

  updateMemberRole: (orgId, userId, role) =>
    apiClient.put(`/organisations/${orgId}/members/${userId}/role`, { role }).then(r => r.data),

  removeMember: (orgId, userId) =>
    apiClient.delete(`/organisations/${orgId}/members/${userId}`).then(r => r.data),

  approveJoinRequest: (orgId, requestId, role) =>
    apiClient.post(`/organisations/${orgId}/join-requests/${requestId}/approve`, { role }).then(r => r.data),

  rejectJoinRequest: (orgId, requestId) =>
    apiClient.post(`/organisations/${orgId}/join-requests/${requestId}/reject`).then(r => r.data),

  getOrgEvents: (orgId) =>
    apiClient.get(`/organisations/${orgId}/events`).then(r => r.data),

  getOrgAudit: (orgId, params = {}) =>
    apiClient.get(`/organisations/${orgId}/audit`, { params }).then(r => r.data),

  // ── Landing page ───────────────────────────────────────────────────────────

  submitContactForm: (payload) =>
    apiClient.post('/contact', payload).then(r => r.data),

  pingPresence: (eventId) =>
    apiClient.post(`/events/${eventId}/presence`).then(r => r.data).catch(() => null),

  getPresence: (eventId) =>
    apiClient.get(`/events/${eventId}/presence`).then(r => r.data),
};

// One-time push at module load (app boot) - covers a session that was already
// logged in (localStorage already has a token) before this module ever runs,
// e.g. an app update installed over an existing session: native's token store
// starts empty and nothing else would otherwise trigger a sync until the next
// explicit login/logout.
syncNativeAuthToken();

export default api;
